<template>
  <div>
    <validation-observer
      ref="formValidation"
      v-slot="{ handleSubmit, invalid }"
    >
      <b-alert
        v-if="isProgrammaticBuyerCampaign"
        class="m-3"
        show
        variant="warning"
      >
        <h6 class="mb-0">
          <font-awesome-icon
            :icon="['fas', 'exclamation-triangle']"
            class="mr-2"
          />
          Please reach out to support to make changes to the request and response parsing setup in a custom Programmatic buyer campaign.
        </h6>
      </b-alert>

      <b-form
        novalidate
        @submit.stop.prevent="handleSubmit(showConfirmSubmit)"
      >
        <MainSection
          :categories="categoryList"
          :countries="countries"
          :edit-mode="true"
          :readonly="isProgrammaticBuyerCampaign"
          @category-changed="onCategoryChanged"
        />

        <Destination />

        <AlertProgrammaticTarget
          :routing-type="buyerCampaign.routing.type"
          class="mx-2"
        />

        <DestinationSchedulingCapacity /> 

        <TrafficSources
          :traffic-sources="allowedTrafficSources"
        />

        <Audience />

        <Budget
          :edit="true"
          :invalid="invalid"
          :minimum-initial-deposit-satisfied="isMinimumInitialDepositRequirementSatisfied"
          :selected-category="selectedCategory"
          @submit="showConfirmSubmit"
        />
      </b-form>
    </validation-observer>

    <b-modal
      id="update-buyer-campaign-modal"
      ref="update-buyer-campaign-modal"
      header-class="custom-modal"
      body-class="custom-modal"
      footer-class="custom-modal"
      title="Confirm Update"
      ok-title="Update"
      button-size="sm"
      @ok="submitForm"
    >
      <UpdateCampaignAlert
        :changed-sections="changedReviewSections"
      />

      <p class="mb-0">
        Are you sure you want to update the buyer campaign?
      </p>

      <template #modal-footer>
        <b-button
          type="button"
          variant="secondary"
          size="sm"
          :disabled="isLoading"
          @click="$bvModal.hide('update-buyer-campaign-modal')"
        >
          Cancel
        </b-button>

        <BaseButtonLoader
          :loading="isLoading"
          class="ml-2"
          type="button"
          :custom-button="false"
          size="sm"
          variant="primary"
          @click="submitForm"
        >
          Update
        </BaseButtonLoader>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import cloneDeep from 'lodash.clonedeep'
import isEqual from 'lodash.isequal'

import MainSection from '../campaign/MainSection.vue'
import Destination from '../campaign/Destination.vue'
import DestinationSchedulingCapacity from '../campaign/DestinationSchedulingCapacity.vue'
import TrafficSources from '../campaign/TrafficSources.vue'
import Audience from './Audience.vue'
import Budget from './Budget.vue'
import UpdateCampaignAlert from './UpdateCampaignAlert.vue'
import AlertProgrammaticTarget from './AlertProgrammaticTarget.vue'
import { showErrorMessage, showSuccessMessage } from '@/notification-utils'
import marketBudgetUsageMixin from '@/mixins/market-budget-usage.mixin'
import { campaignRoutingTypes, isSoftphoneCampaign } from '@/store/modules/campaign.store.js'
import { getSelectableCategories } from '@/utils/campaignCategories'

export default {
  name: 'BuyerEditCampaign',

  title: function() {
    return this.id ? (this.buyerCampaign?.name || 'Campaign') : 'Create Campaign'
  },

  components: {
    MainSection,
    Destination,
    DestinationSchedulingCapacity,
    TrafficSources,
    Audience,
    Budget,
    UpdateCampaignAlert,
    AlertProgrammaticTarget,
  },

  mixins: [marketBudgetUsageMixin],

  props: {
    id: {
      type: String,
      default: ''
    },
  },

  data() {
    return {
      isLoading: false,
      originalCampaign: {},
      selectedCategory: null
    }
  },

  computed: {
    ...mapState('common-entities', ['countries', 'categories']),

    ...mapGetters('account', ['allowedTrafficSources', 'allowedCategories']),

    ...mapState('campaign', ['buyerCampaign']),
    ...mapGetters('campaign', ['isProgrammaticBuyerCampaign']),

    ...mapGetters('auth', ['isMinimumInitialDepositRequirementSatisfied']),

    categoryList() {
      return getSelectableCategories(this.allowedCategories, this.categories)
        .sort((a, b) => a.name.localeCompare(b.name))
    },

    // Sections whose changes require the campaign to be re-reviewed by an admin,
    // pausing traffic until approved. Listed in the confirm modal at save time.
    // Must mirror the backend rule in BuyerCampaignService._CheckIfCampaignNeedsToBeReviewed:
    // Category, Country, Language, a reviewable Destination change (see routingNeedsReview),
    // or turning off the minimum call duration. Traffic Sources and Audience do NOT trigger it.
    // Skipped when the campaign is already In Review — the warning is moot there.
    changedReviewSections() {
      // Guard against the window before the original campaign is fetched and cloned,
      // where the diff would otherwise report every section as changed.
      if (!this.originalCampaign.id) {
        return []
      }

      if (this.isProgrammaticBuyerCampaign || this.buyerCampaign.status === 'InReview') {
        return []
      }

      const original = this.originalCampaign
      const current = this.buyerCampaign

      const reviewableSections = [
        { label: 'Category', changed: original.categoryId !== current.categoryId },
        { label: 'Country', changed: original.countryId !== current.countryId },
        { label: 'Language', changed: original.languageId !== current.languageId },
        { label: 'Destination', changed: this.routingNeedsReview(original.routing, current.routing) },
        {
          label: 'Minimum Call Duration',
          changed: original.budget?.minimumDurationSelected === true
            && current.budget?.minimumDurationSelected === false
        }
      ]

      return reviewableSections
        .filter(({ changed }) => changed)
        .map(({ label }) => label)
    },
  },

  async created() {
    this.setDefaultBuyerCampaign()
    await this.fetchBuyerCampaign(this.id)

    // This page's Destination section only offers Number and SIP, so it cannot represent - and
    // must not be allowed to overwrite - a softphone destination. Covers direct URLs and
    // bookmarks; the campaigns list already links softphone campaigns to the right page.
    if (isSoftphoneCampaign(this.buyerCampaign)) {
      this.$router.replace({ name: 'BuyerEditSoftphoneCampaign', params: { id: this.id } })

      return
    }

    document.title = this.buyerCampaign.name
    await this.getCountries()
    await this.getCategories()
    this.originalCampaign = cloneDeep(this.buyerCampaign)
  },

  beforeMount() {    
    window.addEventListener("beforeunload", this.preventNavigation)
  },

  beforeDestroy() {    
    window.removeEventListener("beforeunload", this.preventNavigation)
  },

  beforeRouteLeave (to, from , next) {    
    if (this.hasUnsavedChanges()) {   
      const confirm = window.confirm('You have unsaved changes. Do you confirm leaving the page?')
      if (confirm) {
        next()
      } else {
        next(false)
      }
    }
    else {
      next()
    }    
  },

  methods: {
    // Also, the list of categories and countries returned should
    // be specific to a buyer. The API endpoint should use the
    // id of the logged in user to fetch specific categories and countries
    // TODO: Set allowed countries for an account?
    ...mapActions('common-entities', ['getCountries', 'getCategories']),

    ...mapActions('campaign', ['setDefaultBuyerCampaign', 'editBuyerCampaign', 'fetchBuyerCampaign']),

    onCategoryChanged(category) {
      this.selectedCategory = category
    },

    // Mirrors the backend CampaignRouting.CheckIfObjectNeedsReview: only a routing
    // type change, an RTB target (RTBId) change, or a Static destination change
    // (type, number, or SIP endpoint) forces re-review. Programmatic target edits do not.
    routingNeedsReview(original, current) {
      if (!original || !current) {
        return false
      }

      if (original.type !== current.type) {
        return true
      }

      switch (current.type) {
      case campaignRoutingTypes.RingbaRTB:
        return original.ringbaRTB?.rtbId !== current.ringbaRTB?.rtbId

      case campaignRoutingTypes.Static:
        if (original.destinationType !== current.destinationType) {
          return true
        }

        return current.destinationType === 'Number'
          ? original.number !== current.number
          : !isEqual(original.sipNumber, current.sipNumber)

      default:
        return false
      }
    },

    preventNavigation(event) {      
      if (this.hasUnsavedChanges()) {
        event.preventDefault()
        event.returnValue = ""
      }      
    },

    showConfirmSubmit() {
      this.$bvModal.show('update-buyer-campaign-modal')
    },

    async submitForm() {
      try {
        this.isLoading = true

        await this.editBuyerCampaign(cloneDeep(this.buyerCampaign))

        // Before the reload below, which drops what the market card recorded
        this.trackMarketBudgetSaved({ original: this.originalCampaign, campaignId: this.id })

        showSuccessMessage('Campaign updated successfully')

        this.$bvModal.hide('update-buyer-campaign-modal')

        await this.fetchBuyerCampaign(this.id)

        this.originalCampaign = cloneDeep(this.buyerCampaign)
      } catch (error) {
        showErrorMessage(error.response?.data?.title || error.message)
      } finally {
        this.isLoading = false
      }
    },

    hasUnsavedChanges() {
      return !isEqual(this.originalCampaign, this.buyerCampaign)
    }
  }
}
</script>

<style lang="scss" scoped></style>
